import {
    Autocomplete,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    Typography
} from "@mui/material";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import React, {useEffect, useState} from "react";
import Cookies from "js-cookie";
import {routes, useGetDataList, usePostData} from "../routes/api";
import Button from "@mui/material/Button";
import ArticleIcon from '@mui/icons-material/Article';
import Box from "@mui/material/Box";

export default function ExternalAIModelDialog({dialogOpen, handleDialogClose, SNtoPK}) {
    const token = Cookies.get('token');
    const route = routes.aimodelexternal;

    const [open, setOpen] = useState(false);
    const [selectedCamera, setSelectedCamera] = useState(Object.values(SNtoPK));
    const [selectedModel, setSelectedModel] = useState('');
    const [fetchedID, setFetchedID] = useState([]);

    const {
        data: modelData,
        loading: getLoading,
        statusCode: getStatus,
        handleCall: getModelList
    } = useGetDataList(token, route);
    const {
        data: postData,
        loading: postLoading,
        statusCode: postStatus,
        handleCall: postExternalModel
    } = usePostData(token, route);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await getModelList({});
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        }
        if (dialogOpen) {
            fetchData();
        }
    }, [dialogOpen]);

    useEffect(() => {
        if (JSON.stringify(modelData) !== '{}') {
            setFetchedID(modelData.map((item) => ([item.id, item.name])));
        }
    }, [modelData, getStatus]);

    const handleChange = (newValue) => {
        if (!newValue) {
            setSelectedModel('');
        } else {
            setSelectedModel(newValue);
        }
    }

    const handleSelectSensor = (e, PK) => {
        if (e.target.checked) {
            setSelectedCamera((c) => ([...c, PK]));
        } else {
            setSelectedCamera((c) => (c.filter((item) => item !== PK)))
        }
    }

    const handleSubmit = async () => {
        await postExternalModel({
            body: {
                model_id: selectedModel[0],
                camera_pks: selectedCamera,
            }
        });
        handleDialogClose();
    }

    if (getLoading) {
        return <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            aria-labelledby="popup-dialog-title"
            aria-describedby="popup-dialog-description"
            fullWidth
            maxWidth='xs'
        >
            <DialogTitle>
                Select External AI Model
            </DialogTitle>
            <DialogContent
                sx={{
                    minWidth: '270px',
                    minHeight: '110px',
                    mt: 2,
                    overflow: 'visible'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 5,
                        mb: 5
                    }}
                >
                    <CircularProgress/>
                </Box>
            </DialogContent>
        </Dialog>
    }

    if (fetchedID.length === 0 || !SNtoPK) {
        return <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            aria-labelledby="popup-dialog-title"
            aria-describedby="popup-dialog-description"
            fullWidth
            maxWidth='xs'
        >
            <DialogTitle>
                Select External AI Model
            </DialogTitle>
            <DialogContent
                sx={{
                    minWidth: '270px',
                    minHeight: '110px',
                    mt: 2,
                    overflow: 'visible'
                }}
            >
                <Typography variant="subtitle1" color="grey"
                            sx={{fontSize: '1.1rem', display: 'flex', alignItems: 'center'}}>
                    <IconButton disableRipple>
                        <ArticleIcon sx={{fontSize: 30}}/>
                    </IconButton>
                    No Available AI Model
                </Typography>
            </DialogContent>
        </Dialog>
    }

    return (
        <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            aria-labelledby="popup-dialog-title"
            aria-describedby="popup-dialog-description"
            fullWidth
            maxWidth='xs'
        >
            <DialogTitle>
                Update External AI Model
            </DialogTitle>
            <DialogContent
                sx={{
                    minWidth: '270px',
                    minHeight: '110px',
                    overflow: 'visible'
                }}
            >
                <Typography variant="caption" sx={{fontSize: '0.85rem'}}>
                    Select Cameras
                </Typography>
                <FormGroup row sx={{display: 'flex', justifyContent: 'space-between'}}>
                    {Object.entries(SNtoPK).map(([SN, PK]) => (
                        <FormControlLabel
                            key={SN}
                            control={<Checkbox/>}
                            label={SN}
                            checked={selectedCamera.includes(PK)}
                            onChange={(e) => handleSelectSensor(e, PK)}
                        />
                    ))}
                </FormGroup>
                <Typography variant="caption" sx={{mt: 5, fontSize: '0.85rem'}}>
                    Select External Model
                </Typography>
                <Autocomplete
                    id='external-ai-model'
                    open={open}
                    onOpen={() => (setOpen(true))}
                    onClose={() => (setOpen(false))}
                    name='external-ai-model'
                    isOptionEqualToValue={(option, v) => option[0] === v[0]}
                    getOptionLabel={(option) => option[1]}
                    value={selectedModel || null}
                    options={fetchedID}
                    onChange={(e, newValue) => {
                        handleChange(newValue);
                    }}
                    loading={getLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {getLoading ? <CircularProgress color="inherit" size={20}/> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }
                            }}
                        />
                    )}
                    sx={{mt: 0.5}}
                />
                <Button component="label"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedModel || selectedCamera.length === 0}
                        sx={{margin: 0.5, mt: 3}}>
                    Save
                </Button>
            </DialogContent>
        </Dialog>
    );
}